import { useCallback, useState } from 'react'
import { useAI as useAIContext } from '../contexts/AIContext'

interface UseAIOptions {
  onSuccess?: (response: string) => void
  onError?: (error: string) => void
  stream?: boolean
}

export function useAI(options?: UseAIOptions) {
  const context = useAIContext()
  const [streamedContent, setStreamedContent] = useState('')

  const sendMessage = useCallback(async (content: string, tools?: string[]) => {
    setStreamedContent('')
    
    try {
      await context.sendMessage(content, {
        tools,
        onStream: options?.stream ? (chunk) => {
          setStreamedContent(prev => prev + chunk)
        } : undefined
      })
      
      if (options?.onSuccess) {
        const lastMessage = context.messages[context.messages.length - 1]
        if (lastMessage?.role === 'assistant') {
          options.onSuccess(lastMessage.content)
        }
      }
    } catch (error: unknown) {
      if (options?.onError) {
        options.onError(error instanceof Error ? error.message : 'Unknown error')
      }
    }
  }, [context, options])

  const askQuestion = useCallback(async (question: string) => {
    return sendMessage(question)
  }, [sendMessage])

  const executeCommand = useCallback(async (command: string, tools: string[]) => {
    return sendMessage(command, tools)
  }, [sendMessage])

  return {
    ...context,
    sendMessage,
    askQuestion,
    executeCommand,
    streamedContent,
    isStreaming: options?.stream && context.isLoading
  }
}